import BaseService from './BaseService';
import {
  ExperimentReport,
  OrdersResponse,
  Period,
  ShippingResponse,
  StaffCustomer,
  Taker,
  StockRule,
  UnswappedResponse,
  UpsellPreview,
  UpsellsResponse,
  UpsellVersion,
} from 'pages/PostPurchase/types';

// Client for the post-purchase upsells admin API (sbly-dash-collector
// src/sbly-api-server/postPurchase/postPurchaseAdminController.js). Writes
// throw rather than return null: a switch that silently failed to save would
// read as a switch that did.
class PostPurchaseService extends BaseService {
  basePath = 'post-purchase';

  async getUpsells(period: Period, store: string) {
    await this._getAccessToken();

    return this.handleHttpRequest<UpsellsResponse>(
      () => this.http.get(`${this.basePath}/upsells`, { params: { ...period, store } }),
      'Failed to fetch post-purchase upsells',
    );
  }

  async getPreview() {
    await this._getAccessToken();

    return this.handleHttpRequest<{ store: string; upsells: UpsellPreview[] }>(
      () => this.http.get(`${this.basePath}/preview`),
      'Failed to load the upsell previews',
    );
  }

  async getUnswapped(store: string) {
    await this._getAccessToken();

    return this.handleHttpRequest<UnswappedResponse>(
      () => this.http.get(`${this.basePath}/unswapped`, { params: { store } }),
      'Failed to check for unswapped upsell orders',
    );
  }

  // The orders that accepted one design of an upsell in the period.
  async getTakers(upsellId: string, designId: string, period: Period, store: string) {
    await this._getAccessToken();

    return this.handleHttpRequest<{ domain: string; takers: Taker[] }>(
      () => this.http.get(`${this.basePath}/upsells/${upsellId}/takers`, { params: { designId, ...period, store } }),
      'Failed to load the orders that took this upsell',
    );
  }

  // Every upsold order in the period, with its estimated and actual extra freight.
  async getShipping(period: Period, store: string) {
    await this._getAccessToken();

    return this.handleHttpRequest<ShippingResponse>(
      () => this.http.get(`${this.basePath}/shipping`, { params: { ...period, store } }),
      'Failed to load the upsold orders\' shipping',
    );
  }

  // The last `hours` of orders, each with what the upsells did and should have done.
  async getOrders(hours: number) {
    await this._getAccessToken();

    return this.handleHttpRequest<OrdersResponse>(
      () => this.http.get(`${this.basePath}/orders`, { params: { hours } }),
      'Failed to load the orders',
    );
  }

  // Every version of an upsell that shipped, with how each looked.
  async getVersions(upsellId: string) {
    await this._getAccessToken();
    const response = await this.http.get<{ upsellId: string; versions: UpsellVersion[] }>(`${this.basePath}/upsells/${upsellId}/versions`);
    return response.data.versions;
  }

  // A new A/B test of an upsell, as a draft.
  async createExperiment(upsellId: string, test: { label: string; versions: string[]; weights: Record<string, number> }) {
    await this._getAccessToken();
    const response = await this.http.post<ExperimentReport>(`${this.basePath}/upsells/${upsellId}/experiments`, test);
    return response.data;
  }

  // Starts a draft ({ status: 'running', weights? }), stops a running test
  // ({ status: 'stopped' }) or shows one version to everyone ({ forceVersion },
  // null undoes it).
  async setExperiment(
    upsellId: string,
    experimentId: string,
    change: { status?: 'running' | 'stopped'; weights?: Record<string, number>; forceVersion?: string | null },
  ) {
    await this._getAccessToken();
    await this.http.put(`${this.basePath}/upsells/${upsellId}/experiments/${experimentId}`, change);
  }

  // A draft only.
  async deleteExperiment(upsellId: string, experimentId: string) {
    await this._getAccessToken();
    await this.http.delete(`${this.basePath}/upsells/${upsellId}/experiments/${experimentId}`);
  }

  // Only the switches named change.
  async setSwitches(id: string, switches: { enabled?: boolean; staffOnly?: boolean; stockRule?: StockRule }) {
    await this._getAccessToken();
    await this.http.put(`${this.basePath}/upsells/${id}`, switches);
  }

  // `lookup` is an email address or a Shopify customer id; the API finds the
  // dosaze.com customer. Answers the whole list after the change.
  async addStaff(lookup: string, name: string) {
    await this._getAccessToken();
    const response = await this.http.post<{ staff: StaffCustomer[] }>(`${this.basePath}/staff`, { lookup, name });
    return response.data.staff;
  }

  async removeStaff(customerId: number) {
    await this._getAccessToken();
    const response = await this.http.delete<{ staff: StaffCustomer[] }>(`${this.basePath}/staff/${customerId}`);
    return response.data.staff;
  }
}

export default PostPurchaseService;
